import { useDroppable } from "@dnd-kit/core";
import { Loader2, Plus } from "lucide-react";
import { useEffect, useRef, useState, type KeyboardEvent, type MouseEvent, type RefObject } from "react";
import { useTranslation } from "react-i18next";

import { DraggableKanbanCard } from "@/components/kanban/KanbanCard";
import { TaskForm } from "@/components/kanban/TaskForm";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import type { KanbanColumnView } from "@/hooks/use-kanban-tasks";
import type {
  Tag,
  Task,
  TaskStatus,
  TaskTitleSource,
  TaskUpdateRequest,
  TenantUser,
} from "@/lib/project-types";
import {
  isTaskStatus,
  TASK_STATUS_DOT_CLASSES,
  TASK_STATUS_LABEL_KEYS,
  TASK_STATUS_SHAPE_CLASSES,
} from "@/lib/task-status";
import { cn } from "@/lib/utils";

const COLUMN_ID_PREFIX = "kanban-column:";
const LOAD_MORE_ROOT_MARGIN = "0px 0px 320px 0px";

export function kanbanColumnId(status: TaskStatus): string {
  return `${COLUMN_ID_PREFIX}${status}`;
}

export function statusFromKanbanColumnId(id: string): TaskStatus | null {
  const status = id.startsWith(COLUMN_ID_PREFIX)
    ? id.slice(COLUMN_ID_PREFIX.length)
    : "";
  return isTaskStatus(status) ? status : null;
}

export interface KanbanColumnProps {
  status: TaskStatus;
  column: KanbanColumnView;
  onCardClick: (task: Task) => void;
  selectedTaskIds?: ReadonlySet<string>;
  onSelectionClick?: (task: Task, event: MouseEvent | KeyboardEvent) => boolean;
  onDeleteTask: (taskId: string) => void;
  onAddSubtask?: (task: Task) => void;
  hierarchySupported?: boolean;
  onCreateTask: (
    title: string,
    status: TaskStatus,
    assignedTo?: string | null,
    tagIds?: string[],
    dueDate?: string | null,
    titleSource?: TaskTitleSource,
  ) => unknown | Promise<unknown>;
  onUpdateTask?: (
    taskId: string,
    updates: TaskUpdateRequest,
  ) => Promise<boolean>;
  allTags?: Tag[];
  onUpdateTags?: (taskId: string, tagIds: string[]) => Promise<boolean>;
  onCreateTag?: (name: string, color?: string) => Promise<Tag | null>;
  onArchiveTag?: (tagId: string) => Promise<boolean>;
  onRestoreTag?: (tagId: string) => Promise<boolean>;
  onLoadMore: (status: TaskStatus) => void | Promise<void>;
  onRetry: (status: TaskStatus) => void | Promise<void>;
  onRevealPending: (status: TaskStatus) => void;
  scrollContainerRef?: RefObject<HTMLElement | null>;
  tenantUsers?: TenantUser[];
  defaultAssigneeId?: string | null;
  dueDateSupported?: boolean;
  timezone?: string;
  agentName?: string;
  agentIdForTask?: (task: Task) => string | undefined;
  defaultAgentId?: string | null;
  agentNameForTask?: (task: Task) => string | undefined;
  activeTaskId?: string | null;
  scrollToTaskId?: string | null;
  /** One-shot signal used by the project-folder plus action. */
  shouldOpenCreateForm?: boolean;
  /** Lets the route owner consume the one-shot create-form signal. */
  onCreateFormOpened?: () => void;
  className?: string;
}

/** One fixed task-status lane with independent cursor pagination. */
export function KanbanColumn({
  status,
  column,
  onCardClick,
  selectedTaskIds,
  onSelectionClick,
  onDeleteTask,
  onAddSubtask,
  hierarchySupported = false,
  onCreateTask,
  onUpdateTask,
  allTags = [],
  onUpdateTags,
  onCreateTag,
  onArchiveTag,
  onRestoreTag,
  onLoadMore,
  onRetry,
  onRevealPending,
  scrollContainerRef,
  tenantUsers = [],
  defaultAssigneeId,
  dueDateSupported = false,
  timezone = "Europe/Zurich",
  agentName,
  agentIdForTask,
  defaultAgentId,
  agentNameForTask,
  activeTaskId = null,
  scrollToTaskId = null,
  shouldOpenCreateForm = false,
  onCreateFormOpened,
  className,
}: KanbanColumnProps) {
  const { t } = useTranslation();
  const { setNodeRef, isOver } = useDroppable({
    id: kanbanColumnId(status),
    data: { type: "kanban-column", status },
  });
  const [adding, setAdding] = useState(false);
  const [observerUnavailable, setObserverUnavailable] = useState(false);
  const handledCreateSignalRef = useRef(false);
  const bottomSentinelRef = useRef<HTMLDivElement>(null);
  const taskElementRefs = useRef(new Map<string, HTMLDivElement>());
  const lastScrolledTaskIdRef = useRef<string | null>(null);
  const statusLabel = t(TASK_STATUS_LABEL_KEYS[status]);
  const { tasks } = column;

  const openCreateForm = () => setAdding(true);

  useEffect(() => {
    if (!shouldOpenCreateForm) {
      handledCreateSignalRef.current = false;
      return;
    }
    if (handledCreateSignalRef.current) return;
    handledCreateSignalRef.current = true;
    setAdding(true);
    onCreateFormOpened?.();
  }, [onCreateFormOpened, shouldOpenCreateForm]);

  useEffect(() => {
    const sentinel = bottomSentinelRef.current;
    if (
      !sentinel
      || !column.hasMore
      || column.initialLoading
      || column.loadingMore
      || column.error
    ) return;

    if (typeof IntersectionObserver === "undefined") {
      setObserverUnavailable(true);
      return;
    }

    setObserverUnavailable(false);
    try {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            void onLoadMore(status);
          }
        },
        {
          root: scrollContainerRef?.current ?? null,
          rootMargin: LOAD_MORE_ROOT_MARGIN,
          threshold: 0,
        },
      );
      observer.observe(sentinel);
      return () => observer.disconnect();
    } catch {
      setObserverUnavailable(true);
    }
  }, [
    column.error,
    column.hasMore,
    column.initialLoading,
    column.loadingMore,
    onLoadMore,
    scrollContainerRef,
    status,
    tasks.length,
  ]);

  useEffect(() => {
    if (!scrollToTaskId) {
      lastScrolledTaskIdRef.current = null;
      return;
    }
    // Polling refreshes task-array references, but must not repeatedly move
    // the board back to the selected card.
    const taskElement = taskElementRefs.current.get(scrollToTaskId);
    if (!taskElement) {
      lastScrolledTaskIdRef.current = null;
      return;
    }
    if (lastScrolledTaskIdRef.current === scrollToTaskId) return;
    const frame = requestAnimationFrame(() => {
      taskElement.scrollIntoView({
        block: "center",
        inline: "nearest",
      });
      lastScrolledTaskIdRef.current = scrollToTaskId;
    });
    return () => cancelAnimationFrame(frame);
  }, [scrollToTaskId, tasks]);

  const loadedCountLabel = column.hasMore
    ? t("projects:taskLoadedCountMoreAvailable", { count: tasks.length })
    : t("projects:taskCount", { count: tasks.length });

  return (
    <section
      ref={setNodeRef}
      className={cn(
        "flex min-w-0 flex-col rounded-lg border bg-muted/20 p-3 transition-colors",
        isOver && "border-primary bg-primary/5 ring-2 ring-primary/20",
        className,
      )}
      aria-label={statusLabel}
      data-testid={`kanban-column-${status}`}
    >
      <header className="mb-3 flex items-center gap-2 px-1">
        <span
          className={cn(
            "size-2.5 shrink-0 rounded-full",
            TASK_STATUS_DOT_CLASSES[status],
            TASK_STATUS_SHAPE_CLASSES[status],
          )}
          aria-hidden="true"
        />
        <h3 className="min-w-0 flex-1 truncate text-sm font-semibold">
          {statusLabel}
        </h3>
        <Badge
          variant="secondary"
          className="shrink-0 tabular-nums"
          aria-label={loadedCountLabel}
        >
          {tasks.length}{column.hasMore ? "+" : ""}
        </Badge>
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          aria-label={t("projects:addTaskToColumn", { status: statusLabel })}
          onClick={openCreateForm}
        >
          <Plus />
        </Button>
      </header>

      {adding && (
        <TaskForm
          key={status}
          mode="create"
          status={status}
          tenantUsers={tenantUsers}
          defaultAssigneeId={defaultAssigneeId}
          dueDateSupported={dueDateSupported}
          allTags={allTags}
          onCreateTag={onCreateTag}
          onCreateTask={onCreateTask}
          onCancel={() => setAdding(false)}
          onSaved={() => setAdding(false)}
        />
      )}

      {column.pendingNewCount > 0 && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="mb-3 self-center"
          onClick={() => {
            onRevealPending(status);
            scrollContainerRef?.current?.scrollTo?.({ top: 0, behavior: "smooth" });
          }}
        >
          {t("projects:newTasksAvailable", {
            count: column.pendingNewCount,
          })}
        </Button>
      )}

      {column.initialLoading && tasks.length === 0 ? (
        <div
          className="grid gap-2"
          role="status"
          aria-label={t("projects:loadingTasks")}
        >
          {[0, 1, 2].map((index) => (
            <div
              key={index}
              className="h-24 animate-pulse rounded-lg border bg-muted/70"
            />
          ))}
        </div>
      ) : column.error && tasks.length === 0 ? (
        <div className="grid gap-2 rounded-lg border border-dashed px-3 py-6 text-center">
          <p className="text-sm text-destructive">
            {t("projects:taskLoadFailed")}
          </p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => {
              void onRetry(status);
            }}
          >
            {t("common:actions.retry")}
          </Button>
        </div>
      ) : (
        <div
          className="flex-1"
          data-testid={`kanban-column-items-${status}`}
        >
          {tasks.map((task) => (
            <div
              key={task.id}
              ref={(element) => {
                if (element) taskElementRefs.current.set(task.id, element);
                else taskElementRefs.current.delete(task.id);
              }}
              className="pb-2"
            >
              <DraggableKanbanCard
                task={task}
                agentName={agentNameForTask?.(task) ?? agentName}
                agentId={agentIdForTask?.(task)}
                defaultAgentId={defaultAgentId}
                active={task.id === activeTaskId}
                selected={selectedTaskIds?.has(task.id)}
                onSelectionClick={onSelectionClick}
                onClick={onCardClick}
                onDeleteTask={onDeleteTask}
                onAddSubtask={onAddSubtask}
                hierarchySupported={hierarchySupported}
                onUpdateTask={onUpdateTask}
                allTags={allTags}
                onUpdateTags={onUpdateTags}
                onCreateTag={onCreateTag}
                onArchiveTag={onArchiveTag}
                onRestoreTag={onRestoreTag}
                tenantUsers={tenantUsers}
                dueDateSupported={dueDateSupported}
                timezone={timezone}
              />
            </div>
          ))}
          {tasks.length === 0 && !adding && (
            <p className="rounded-lg border border-dashed px-3 py-6 text-center text-sm text-muted-foreground">
              {t("projects:noTasks")}
            </p>
          )}
          <div
            ref={bottomSentinelRef}
            className="h-px"
            data-testid={`kanban-column-sentinel-${status}`}
            aria-hidden="true"
          />
          {column.loadingMore && (
            <div
              className="flex items-center justify-center gap-2 py-3 text-sm text-muted-foreground"
              aria-live="polite"
            >
              <Loader2 className="size-3.5 animate-spin" aria-hidden="true" />
              {t("projects:loadingMoreTasks")}
            </div>
          )}
          {column.error && tasks.length > 0 && (
            <div className="grid justify-items-center gap-2 py-3">
              <p className="text-center text-sm text-destructive">
                {t("projects:taskLoadFailed")}
              </p>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  void onRetry(status);
                }}
              >
                {t("common:actions.retry")}
              </Button>
            </div>
          )}
          {observerUnavailable
            && column.hasMore
            && !column.loadingMore
            && !column.error && (
            <div className="flex justify-center py-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => {
                  void onLoadMore(status);
                }}
              >
                {t("projects:loadMoreTasks")}
              </Button>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
